/*
  EVO 1RM-Rechner — vanilla port of the old Vue widget (one-rep-max-calculator.js + strengthCalculations.js).
  Logic is kept 1:1: same seven formulas in the same order, plain average, same guards
  (no weight / no reps / more than 36 reps → no result, result only if > 0), same percentage table
  (1–10 RM), same rounding (toFixed(1)). Only the number display uses a German decimal comma.
*/
(() => {
  // same order as strengthCalculations.js — keeps the floating-point sum bit-identical
  const FORMULAS = [
    ['Lombardi', (w, r) => w * Math.pow(r, 0.1)],
    ['Brzycki', (w, r) => w * (36 / (37 - r))],
    ['Epley', (w, r) => w * (1 + r / 30)],
    ['Mayhew', (w, r) => (100 * w) / (52.2 + 41.9 * Math.exp(-0.055 * r))],
    ["O'Conner", (w, r) => w * (1 + 0.025 * r)],
    ['Wathan', (w, r) => (100 * w) / (48.8 + 53.8 * Math.exp(-0.075 * r))],
    ['Lander', (w, r) => (100 * w) / (101.3 - 2.67123 * r)],
  ];

  const MAX_REPS = 36;

  // percent of 1RM per repetition maximum (1RM … 10RM)
  const PERCENT = { 1: 100, 2: 95, 3: 90, 4: 88, 5: 86, 6: 83, 7: 80, 8: 78, 9: 76, 10: 75 };

  const formulaValues = (w, r) => FORMULAS.map(([, f]) => f(w, r));

  const oneRepMax = (w, r) => {
    if (!w || !r || r > MAX_REPS) return 0;
    const values = formulaValues(w, r);
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  };

  const repWeight = (orm, reps) => (orm * PERCENT[reps]) / 100;

  const fmt = (x) => x.toFixed(1).replace('.', ',');
  const fmtInput = (x) => String(x).replace('.', ',');

  // "62,5" and "62.5" both work; anything that isn't a plain number counts as invalid (like a type=number field)
  const parse = (raw) => {
    const s = String(raw).replace(/\s+/g, '').replace(',', '.');
    if (s === '') return null;
    if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(s)) return NaN;
    return Number(s);
  };

  const init = (root) => {
    if (root.dataset.ormReady) return;
    root.dataset.ormReady = 'true';

    const weightInput = root.querySelector('[data-1rm-weight]');
    const repsInput = root.querySelector('[data-1rm-reps]');
    const weightError = root.querySelector('[data-1rm-weight-error]');
    const repsError = root.querySelector('[data-1rm-reps-error]');
    const card = root.querySelector('[data-1rm-result]');
    const valueEl = root.querySelector('[data-1rm-value]');
    const setEl = root.querySelector('[data-1rm-set]');
    const rangeEl = root.querySelector('[data-1rm-range]');
    const tbody = root.querySelector('[data-1rm-table] tbody');
    const tableHint = root.querySelector('[data-1rm-table-hint]');
    if (!weightInput || !repsInput || !card || !tbody) return;

    // build the 1–10 RM rows once; the weights get filled in on every update
    tbody.innerHTML = '';
    const weightCells = {};
    for (let reps = 1; reps <= 10; reps++) {
      const tr = document.createElement('tr');
      const th = document.createElement('th');
      th.scope = 'row';
      th.textContent = `${reps}RM`;
      const pct = document.createElement('td');
      pct.textContent = `${PERCENT[reps]} %`;
      const kg = document.createElement('td');
      kg.textContent = '–';
      tr.append(th, pct, kg);
      tbody.append(tr);
      weightCells[reps] = kg;
    }

    const flag = (input, errorEl, invalid) => {
      input.setAttribute('aria-invalid', invalid ? 'true' : 'false');
      if (!errorEl) return;
      errorEl.hidden = !invalid;
      errorEl.textContent = invalid ? errorEl.dataset.msg || '' : '';
    };

    const update = () => {
      const w = parse(weightInput.value);
      const r = parse(repsInput.value);

      // inline hints only for entries that can never give a result (empty fields stay quiet)
      flag(weightInput, weightError, w !== null && !(w > 0));
      flag(repsInput, repsError, r !== null && !(r > 0 && r <= MAX_REPS));

      const orm = w === null || r === null || Number.isNaN(w) || Number.isNaN(r) ? 0 : oneRepMax(w, r);
      const filled = orm > 0;

      if (!filled) {
        card.setAttribute('data-empty', '');
        root.removeAttribute('data-1rm-filled');
        for (let reps = 1; reps <= 10; reps++) weightCells[reps].textContent = '–';
        if (tableHint) tableHint.hidden = false;
        return;
      }

      const values = formulaValues(w, r);
      card.removeAttribute('data-empty');
      root.setAttribute('data-1rm-filled', '');
      valueEl.textContent = fmt(orm);
      if (setEl) setEl.textContent = `${fmtInput(w)} kg × ${fmtInput(r)} Wdh.`;
      if (rangeEl) rangeEl.textContent = `${fmt(Math.min(...values))}–${fmt(Math.max(...values))} kg`;
      for (let reps = 1; reps <= 10; reps++) weightCells[reps].textContent = `${fmt(repWeight(orm, reps))} kg`;
      if (tableHint) tableHint.hidden = true;
    };

    weightInput.addEventListener('input', update);
    repsInput.addEventListener('input', update);
    update();
  };

  const boot = (scope) => scope.querySelectorAll('[data-evo-1rm]').forEach(init);

  boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
